/**
 * Loads super-admin provider and facility metadata into shared auth state.
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FirestoreService } from "../firebase/firestoreService";
import { FirestoreCollectionReference } from "../firebase/references";
import type {
  HealthCareFacilityModel,
  SuperAdminProvider,
  FacilityInfo,
  SuperAdminProvidersState,
} from "../types";
import type { AuthAppDispatch } from "./store";

const initialState: SuperAdminProvidersState = {
  providers: [],
  facilities: [],
  loading: false,
  error: null,
};

const superAdminProvidersSlice = createSlice({
  name: "superAdminProviders",
  initialState,
  reducers: {
    fetchStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchProvidersSuccess(state, action: PayloadAction<SuperAdminProvider[]>) {
      state.providers = action.payload;
      state.loading = false;
    },
    fetchFacilitiesSuccess(state, action: PayloadAction<FacilityInfo[]>) {
      state.facilities = action.payload;
    },
    fetchFailure(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.loading = false;
    },
    resetSuperAdminProviders(state) {
      state.providers = [];
      state.facilities = [];
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  fetchStart,
  fetchProvidersSuccess,
  fetchFacilitiesSuccess,
  fetchFailure,
  resetSuperAdminProviders,
} = superAdminProvidersSlice.actions;

/**
 * Loads facilities and subscribes to provider updates for the super-admin directory.
 *
 * @returns Redux thunk resolving to the provider listener unsubscribe callback
 */
export const fetchAllProvidersForSuperAdmin =
  () => async (dispatch: AuthAppDispatch) => {
    dispatch(fetchStart());

    try {
      const facilities =
        await FirestoreService.getAllDocuments<HealthCareFacilityModel>(
          FirestoreCollectionReference.healthcareFacility(),
        );

      const facilityInfoList: FacilityInfo[] = facilities.map((f) => ({
        id: f.DocId || "",
        name: f.Name || "Unknown Facility",
        isExternal: f.is_external_organization === true,
        dashboardType: f.dashboard_type,
      }));

      dispatch(fetchFacilitiesSuccess(facilityInfoList));

      const unsubscribe =
        FirestoreService.listenToAllDocuments<SuperAdminProvider>(
          FirestoreCollectionReference.providers(),
          (providersData) => {
            dispatch(fetchProvidersSuccess(providersData));
          },
          (error) => {
            dispatch(fetchFailure(error.message));
          },
        );

      return unsubscribe;
    } catch (error) {
      dispatch(fetchFailure((error as Error).message));
      return () => {};
    }
  };

export default superAdminProvidersSlice.reducer;
